import { siteConfig } from '../common/common';
import { ArgSpecDictionary, TypedVars } from './ComponentUtils';

export type DisplayChangeCallback = (element: HTMLElement, visible: boolean, display: string) => void;
export type BreakpointChangeCallback = (breakpoint: string) => void;

const CONNECTION_EVENT_NAME: string = 'component-connected';
const DISCONNECTION_EVENT_NAME: string = 'component-disconnected';

export const Breakpoints = {
	xs: 0,
	sm: 375,
	md: 834,
	lg: 1500,
};

export class Component extends HTMLElement {
	vars: TypedVars = new TypedVars(this);
	protected root: HTMLElement | ShadowRoot;
	private _initialised: boolean = false;
	private queries: { [key: string]: MediaQueryList } = {};
	private _slotChangeFuncs: any = {};

	static _globalStylesAdded: boolean = false;

	private mutationObserver: MutationObserver | null = null;
	private processingMutations: boolean = false;
	private mutationRecords: MutationRecord[] = [];

	protected slottedChildren?: HTMLElement[] | string | null = null;
	protected observer: MutationObserver | null = null;

	constructor() {
		super();
		if (siteConfig.componentsUseShadowDom) {
			this.root = this.attachShadow({ mode: 'open' });
		} else {
			this.root = this;
			if (!this.usingShadow) {
				this.addMutationObserver();
				this.storeChildren();
			}
		}
	}

	private storeChildren(): void {
		if (this.slottedChildren) return;
		const children = this.children as HTMLCollection;
		if (children?.length) {
			this.slottedChildren = Array.from(children) as HTMLElement[];
		} else if (this.innerHTML) {
			this.slottedChildren = this.innerHTML;
		}
	}

	addMutationObserver() {
		this.mutationObserver = new MutationObserver((mutations) => {
			this.mutationRecords.push(...mutations);
			if (!this.processingMutations) {
				this.processingMutations = true;
				Promise.resolve().then(() => {
					this.mutationObserver?.disconnect();
					this.processMutations(this.mutationRecords);
					this.mutationRecords = [];
					this.processingMutations = false;
				});
			}
		});
		this.mutationObserver.observe(this, { childList: true, subtree: true });
	}

	processMutations(mutations: MutationRecord[]) {
		//logger.log('Processing all mutations in one pass:', mutations);
		const addedNodes: HTMLElement[] = [];
		mutations.forEach((mutation) => {
			mutation.addedNodes.forEach((node) => {
				if (node instanceof HTMLElement && customElements.get(node.tagName.toLowerCase())) {
					addedNodes.push(node);
				}
			});
		});
		// Wait for web components to be defined
		this.onSlotChange(null, addedNodes);
	}

	get initialised(): boolean {
		return this._initialised;
	}

	awaitInitialisationOf(target: HTMLElement): Promise<void> | null {
		if (!target) return null;
		return new Promise((resolve) => {
			if ((target as Component)?.initialised) {
				resolve();
			} else {
				const connectionListener = () => {
					document.removeEventListener(CONNECTION_EVENT_NAME, connectionListener);
					resolve();
				};
				document.addEventListener(CONNECTION_EVENT_NAME, connectionListener);
			}
		});
	}

	get usingShadow(): boolean {
		return this.root !== this;
	}

	setInnerHtml(globalStyles: string, css: string, innerHtml: string, additionalStyles: string[] | null = null): void {
		if (this.usingShadow) {
			const additionalStylesHtml = additionalStyles?.map((style) => `<style>${style}</style>`).join('') ?? '';

			this.root.innerHTML = `
			<style>${globalStyles}</style>
			<style>${css}</style>
      ${additionalStylesHtml}
      ${innerHtml}
		`;
		} else {
			if (!Component._globalStylesAdded) {
				const globalStyle = document.createElement('style');
				globalStyle.textContent = globalStyles;
				document.head.appendChild(globalStyle);
				Component._globalStylesAdded = true;
			}

			const style = document.createElement('style');
			style.textContent = css;
			document.head.appendChild(style);

			additionalStyles?.forEach((additionalStyle) => {
				const additionalStyleElement = document.createElement('style');
				additionalStyleElement.textContent = additionalStyle;
				document.head.appendChild(additionalStyleElement);
			});

			this.root.innerHTML = innerHtml;
			this.processLightDomSlots();
		}
	}

	setStoryInnerHtml(html: string): void {
		if (this.usingShadow) {
			this.root.innerHTML = html;
		} else {
			this.root.innerHTML = html;
			this.storeChildren();
			this.processLightDomSlots();
		}
	}

	processLightDomSlots(): void {
		// Find slot elements and replace them with elements from
		// slotted children extracted at component instantiation
		const slots = this.root.querySelectorAll('slot') as NodeListOf<HTMLSlotElement>;
		slots?.forEach((slot) => {
			const slotName = slot.name ?? 'unnamed';
			//logger.log(this.tagName, 'Slot found ' + (slotName ? `(${slotName})` : ''));
			if (typeof this.slottedChildren === 'string') {
				//logger.log(this.tagName + ' slotted manually');
				slot.parentElement?.insertBefore(document.createTextNode(this.slottedChildren), slot);
			} else if (typeof this.slottedChildren === 'object') {
				const slottedChildren = this.slottedChildren?.filter((el) => {
					return el.slot === slotName;
				});
				slottedChildren?.forEach((el) => slot.parentElement?.insertBefore(el, slot));
			}
			slot.remove();
		});
	}

	observeSlotChanges(toggle: boolean): void {
		if (!this.usingShadow) return;

		const slots = this.root.querySelectorAll('slot') as NodeListOf<HTMLSlotElement>;
		if (toggle) {
			slots?.forEach((slot) => {
				const slotName = slot.name ?? 'unnamed';
				if (this._slotChangeFuncs[slotName]) return;
				const slotChangeFunc = (event: Event) => {
					const slot = event.target as HTMLSlotElement;
					this.onSlotChange(slot, slot.assignedElements({ flatten: true }) as HTMLElement[]);
				};
				slot.addEventListener('slotchange', slotChangeFunc);
				this._slotChangeFuncs[slotName] = slotChangeFunc;
			});
		} else {
			slots?.forEach((slot) => {
				const slotName = slot.name ?? 'unnamed';
				const slotChangeFunc = this._slotChangeFuncs[slotName];
				if (!slotChangeFunc) return;
				slot.removeEventListener('slotchange', slotChangeFunc);
				delete this._slotChangeFuncs[slotName];
			});
		}
	}

	get currentBreakpoint(): string | null {
		if (Object.keys(this.queries).length === 0) this.observeBreakpointChanges();
		for (let key in this.queries) {
			if (this.queries[key].matches) return key;
		}
		return null;
	}

	observeBreakpointChanges(callback?: BreakpointChangeCallback): void {
		this.queries = {};

		const queryStringFromTo = (from: number, to: number): string => `(min-width: ${from}px) and (max-width: ${to - 1}px)`;

		const queryFromTo = (from: number, to: number): MediaQueryList => window.matchMedia(queryStringFromTo(from, to));

		const configureMediaQuery = (from: number, to: number, key: string) => {
			const query = queryFromTo(from, to);
			query.addEventListener('change', (_event: MediaQueryListEvent) => {
				if (query.matches) callback && callback(key);
			});
			this.queries[key] = query;
		};

		const entries = Object.entries(Breakpoints);
		for (let i = 0; i < entries.length; i++) {
			const [key, breakpoint] = entries[i];
			if (i === entries.length - 1) {
				configureMediaQuery(breakpoint, 9999, key);
			} else {
				configureMediaQuery(breakpoint, entries[i + 1][1], key);
			}
		}
	}

	observeDisplayOnResize(targetElement: HTMLElement, callback: DisplayChangeCallback): void {
		window.addEventListener('resize', () => {
			const currentDisplay = window.getComputedStyle(targetElement).display;
			callback && callback(targetElement, currentDisplay !== 'none', currentDisplay);
		});
	}

	connectedCallback(): void {
		let argSpecs = (this.constructor as any).argSpecs();
		this.vars.parse(this, argSpecs);
		this.init();
		this._initialised = true;
		this.emitDocumentEvent(CONNECTION_EVENT_NAME, this);
	}

	disconnectedCallback(): void {
		this.destroy();
		this.emitDocumentEvent(DISCONNECTION_EVENT_NAME, this);
		this.vars?.destroy();
		this.observeSlotChanges(false);
		this._slotChangeFuncs = {};
		this.mutationObserver?.disconnect();
		this.mutationObserver = null;
		this.processingMutations = false;
		this.mutationRecords = [];
		this.observer?.disconnect();
		this._initialised = false;
	}

	protected emitDocumentEvent(eventName: string, data: any = null) {
		const event = new CustomEvent(eventName, { detail: data });
		document.dispatchEvent(event);
	}

	protected emitEvent(eventName: string, data: any = null) {
		const event = new CustomEvent(eventName, { detail: data });
		this.dispatchEvent(event);
	}

	element<T extends HTMLElement>(selector: string): T {
		return this.root.querySelector(selector) as T;
	}

	elements<T extends HTMLElement>(selector: string): NodeListOf<T> {
		return this.root.querySelectorAll(selector) as NodeListOf<T>;
	}

	getSlotted<T extends HTMLElement>(selector: string, slotName?: string): T | null {
		let slot: HTMLSlotElement | null = null;
		if (slotName) {
			slot = this.root.querySelector(`slot[name="${slotName}"]`) as HTMLSlotElement;
		} else {
			slot = this.root.querySelector('slot') as HTMLSlotElement;
		}
		if (!slot) return null;
		const assignedElements = slot.assignedElements();
		return assignedElements.find((el) => el.matches(selector)) as T;
	}

	static get observedAttributes() {
		return Object.keys(this.argSpecs());
	}

	attributeChangedCallback(name: string, oldValue: string, newValue: string) {
		if (this.initialised) {
			let argSpecs = (this.constructor as any).argSpecs();
			this.vars.update(name, newValue, argSpecs, this.vars);
			this.onAttributeChanged(name, oldValue, newValue);
		}
	}

	protected reinit(reparseArgs: boolean = false): void {
		this.destroy();
		if (reparseArgs) {
			let argSpecs = (this.constructor as any).argSpecs();
			this.vars.parse(this, argSpecs);
		}
		this.init();
	}

	onAttributeChanged(_name: string, _oldValue: string, _newValue: string): void {
		throw new Error('Method must be overridden.');
	}

	onNonShadowSlotChange(): void {}

	onSlotChange(_slot: HTMLSlotElement | null, _elements: HTMLElement[]): void {
		throw new Error('Method must be overridden.');
	}

	static argSpecs(): ArgSpecDictionary {
		throw new Error('Method must be overridden.');
	}

	protected init(): void {
		throw new Error('Method must be overridden.');
	}

	protected destroy(): void {
		throw new Error('Method must be overridden.');
	}

	static getAriaDescriptionAttributeValueFromId(id: string) {
		return `${id}-description`;
	}

	htmlIfVar<T>(name: string, callbackOrValue: ((token: string) => string) | string): string {
		const token = this.vars.get<T>(name);
		if (token && token.length) {
			if (typeof callbackOrValue === 'function') {
				return callbackOrValue(token);
			} else {
				return callbackOrValue;
			}
		}
		return '';
	}

	htmlIf(name: string, callback: () => string): string {
		const condition = this.vars.get<boolean>(name);
		return condition ? callback() : '';
	}

	protected load2xImage(url: string, callback: (logicalW: string, logicalH: string) => void): Promise<void> {
		const image = new Image();
		image.src = url;
		return new Promise((resolve) => {
			image.addEventListener('load', () => {
				const logicalW = `${image.naturalWidth / 2}px`;
				const logicalH = `${image.naturalHeight / 2}px`;
				callback(logicalW, logicalH);
				resolve();
			});
		});
	}
}

//example use:
// optional(this.vars.get<string>(Attrs.heading), (x) => `<h1>${x}</h1>`)

export function htmlIf(condition: boolean, callback: () => string): string {
	return condition ? callback() : '';
}

export function htmlWith(token: any, callback: (token: string) => string): string {
	if (token) return callback(token);
	return '';
}
